import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { getPostById, createPost, updatePost, triggerRebuild, formatApiError } from "../lib/api";
import { ImageField } from "./ImageField";

const inputCls = "w-full rounded-lg border border-cream-2 px-3 py-2.5 text-ink outline-none focus:border-sage bg-white";

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["clean"],
  ],
};

export const BlogEditor = () => {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [post, setPost] = useState({
    title: "",
    excerpt: "",
    category: "General",
    tags: [],
    cover_image: null,
    body_html: "",
    status: "draft",
    seo_title: "",
    meta_description: "",
  });

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const data = await getPostById(id);
        setPost(data);
      } catch (e) {
        toast.error("Could not load post");
        navigate("/admin/blog");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew, navigate]);

  const tagsString = useMemo(() => (post.tags || []).join(", "), [post.tags]);

  const set = (patch) => setPost((p) => ({ ...p, ...patch }));

  const save = async (status) => {
    if (!post.title.trim()) {
      toast.error("Please add a title");
      return;
    }
    setSaving(true);
    const payload = { ...post, status: status || post.status };
    try {
      if (isNew) {
        const data = await createPost(payload);
        if (data.status === "published") triggerRebuild("blog post published");
        toast.success("Post created");
        navigate(`/admin/blog/${data.id}`);
      } else {
        const data = await updatePost(id, payload);
        setPost(data);
        // Trigger rebuild whenever the post is (or becomes) public so the
        // static/prerendered version + sitemap stay in sync.
        if (data.status === "published") triggerRebuild("blog post updated");
        toast.success("Post saved");
      }
    } catch (err) {
      toast.error(formatApiError(err) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-sage" /></div>;

  return (
    <div className="max-w-3xl">
      <button onClick={() => navigate("/admin/blog")} className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink mb-4"><ArrowLeft className="h-4 w-4" /> Back to posts</button>
      <h1 className="font-serif text-3xl text-ink mb-6">{isNew ? "New post" : "Edit post"}</h1>

      <div className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-ink mb-2">Title</label>
          <input data-testid="post-title" value={post.title} onChange={(e) => set({ title: e.target.value })} className={inputCls} />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink mb-2">Category</label>
            <input data-testid="post-category" value={post.category} onChange={(e) => set({ category: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-2">Tags (comma separated)</label>
            <input data-testid="post-tags" value={tagsString} onChange={(e) => set({ tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} className={inputCls} />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-ink mb-2">Excerpt (shown in the blog list)</label>
          <textarea data-testid="post-excerpt" rows={2} value={post.excerpt} onChange={(e) => set({ excerpt: e.target.value })} className={inputCls} />
        </div>

        <ImageField label="Cover image" value={post.cover_image} onChange={(v) => set({ cover_image: v })} testId="cover" />

        <div>
          <label className="block text-sm font-medium text-ink mb-2">Body</label>
          <div className="bg-white rounded-lg border border-cream-2" data-testid="post-body">
            <ReactQuill theme="snow" value={post.body_html} onChange={(v) => set({ body_html: v })} modules={QUILL_MODULES} />
          </div>
        </div>

        {/* SEO overrides — leave blank to fall back to the post title / excerpt */}
        <div className="rounded-xl border border-cream-2 bg-cream/40 p-5 space-y-4">
          <div>
            <h2 className="font-serif text-lg text-ink">Search-engine overrides</h2>
            <p className="text-xs text-ink-muted mt-0.5">Optional. Leave blank to use the post title and excerpt.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-2">SEO title override</label>
            <input data-testid="post-seo-title" value={post.seo_title || ""} onChange={(e) => set({ seo_title: e.target.value })} className={inputCls} placeholder="e.g., 5 Ways to Ease Anxiety Naturally" />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-2">Meta description override</label>
            <textarea data-testid="post-meta-description" rows={2} value={post.meta_description || ""} onChange={(e) => set({ meta_description: e.target.value })} className={inputCls} placeholder="One or two sentences that appear under the link in Google results." />
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button data-testid="save-draft-btn" onClick={() => save("draft")} disabled={saving} className="inline-flex items-center gap-2 rounded-full border border-cream-2 px-5 py-2.5 text-sm font-semibold hover:bg-cream-2 disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save draft
          </button>
          <button data-testid="publish-btn" onClick={() => save("published")} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-sage text-white px-6 py-2.5 text-sm font-semibold hover:bg-sage-dark disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Publish
          </button>
          <span className="text-sm text-ink-muted">Status: {post.status}</span>
        </div>
      </div>
    </div>
  );
};

export default BlogEditor;
